import React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Grid,
  Typography,
  Chip,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  CheckCircle,
  Security,
  Schedule,
  Palette,
  Build,
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import ModelViewer from "../three/ModelViewer";
import { type Product } from "./ProductCard";

type Props = {
  open: boolean;
  onClose: () => void;
  product: Product | null;
};

const ProductModal: React.FC<Props> = ({ open, onClose, product }) => {
  const { t, i18n } = useTranslation();
  const [detail, setDetail] = React.useState<any | null>(null);
  React.useEffect(() => {
    if (!open || !product?.slug) {
      setDetail(null);
      return;
    }
    fetch(`/data/products/${product.slug}/index.json`, { cache: "no-store" })
      .then((r) => r.json())
      .then((data) => setDetail(data))
      .catch(() => setDetail(null));
  }, [open, product?.slug]);

  const data = React.useMemo(() => {
    if (!product) return null;
    return detail ? { ...product, ...detail } : product;
  }, [product, detail]);
  const [mainTab, setMainTab] = React.useState(0); // 0: Images, 1: 3D
  const [activeImage, setActiveImage] = React.useState(0);
  const [hovering, setHovering] = React.useState(false);
  // bgPos is normalized ratios (0..1) within the displayed image area (not the full container)
  const [bgPos, setBgPos] = React.useState<{ x: number; y: number }>({
    x: 0.5,
    y: 0.5,
  });
  // Lens absolute position in pixels inside the container for precise overlay placement
  const [lensPos, setLensPos] = React.useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const imgRef = React.useRef<HTMLImageElement | null>(null);
  const wrapRef = React.useRef<HTMLDivElement | null>(null);
  const [naturalSize, setNaturalSize] = React.useState<{
    w: number;
    h: number;
  } | null>(null);
  // Base zoom multiplier (will be clamped so that preview background is always >= pane)
  const zoomFactor = 1.8;
  const zoomPaneSize = 360;
  if (!data) return null;

  // Use product images or fallback
  const images: string[] = data.images || [
    data.image,
    data.image.replace(/seed\/p?\d+/, (m: string) => m + "-alt1"),
    data.image.replace(/seed\/p?\d+/, (m: string) => m + "-alt2"),
  ];

  // Check if product has 3D model
  const has3DModel = Boolean(data.model3d);

  // Background image URL for zoom preview (handles spaces/UTF-8 safely)
  const bgImageCss = (() => {
    const url = images[activeImage];
    if (!url) return "none";
    try {
      return `url("${encodeURI(url)}")`;
    } catch {
      return `url("${url}")`;
    }
  })();

  const handleMove: React.MouseEventHandler<HTMLDivElement> = (e) => {
    const container = e.currentTarget as HTMLDivElement;
    const rect = container.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;

    if (!naturalSize) {
      // Fallback: use container ratios
      const x = Math.min(Math.max(cx / rect.width, 0), 1);
      const y = Math.min(Math.max(cy / rect.height, 0), 1);
      setBgPos({ x, y });
      setLensPos({ x: cx, y: cy });
      return;
    }

    // Compute displayed image size inside container (object-fit: contain)
    const containerRatio = rect.width / rect.height;
    const imageRatio = naturalSize.w / naturalSize.h;
    let displayW: number;
    let displayH: number;
    if (containerRatio > imageRatio) {
      // container wider than image: image fits by height
      displayH = rect.height;
      displayW = imageRatio * displayH;
    } else {
      // image fits by width
      displayW = rect.width;
      displayH = displayW / imageRatio;
    }
    const offsetX = (rect.width - displayW) / 2;
    const offsetY = (rect.height - displayH) / 2;

    const relX = Math.min(Math.max((cx - offsetX) / displayW, 0), 1);
    const relY = Math.min(Math.max((cy - offsetY) / displayH, 0), 1);

    setBgPos({ x: relX, y: relY });
    setLensPos({ x: offsetX + relX * displayW, y: offsetY + relY * displayH });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          pr: 6,
          display: "flex",
          alignItems: "center",
          gap: 2,
          fontWeight: 800,
        }}
      >
        {data.title}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 12 }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          background: "linear-gradient(180deg, #ffffff 0%, #fafafa 100%)",
        }}
      >
        <Box sx={{ maxHeight: { md: "75vh" }, overflow: "hidden" }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Tabs
                value={mainTab}
                onChange={(_, v) => setMainTab(v)}
                sx={{ mb: 2 }}
              >
                <Tab label={t("productModal.tabs.images")} />
                <Tab
                  label={t("productModal.tabs.model3d")}
                  disabled={!has3DModel}
                />
              </Tabs>

              {mainTab === 0 && (
                <>
                  <Box
                    sx={{
                      position: "relative",
                      overflow: "visible",
                      mb: 2,
                      maxWidth: { xs: "100%", md: 460 },
                      mx: "auto",
                    }}
                  >
                    <Box
                      ref={wrapRef}
                      onMouseEnter={() => setHovering(true)}
                      onMouseLeave={() => setHovering(false)}
                      onMouseMove={handleMove}
                      sx={{
                        position: "relative",
                        width: "100%",
                        height: { xs: 300, sm: 360, md: 420 },
                        borderRadius: 2,
                        overflow: "hidden",
                        userSelect: "none",
                        border: "1px solid #eee",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <Box
                        component="img"
                        ref={imgRef}
                        src={images[activeImage]}
                        alt={data.title}
                        onLoad={(e: any) => {
                          try {
                            const el = e.currentTarget as HTMLImageElement;
                            setNaturalSize({
                              w: el.naturalWidth,
                              h: el.naturalHeight,
                            });
                          } catch {}
                        }}
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "block",
                          objectFit: "contain",
                        }}
                      />

                      {hovering && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: `${lensPos.y - 36}px`,
                            left: `${lensPos.x - 36}px`,
                            width: 72,
                            height: 72,
                            borderRadius: "50%",
                            border: "2px solid rgba(255,255,255,0.85)",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                            pointerEvents: "none",
                            backdropFilter: "brightness(1.05)",
                          }}
                        />
                      )}
                    </Box>

                    {/* Inline preview next to image */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: { md: "calc(100% + 16px)" },
                        width: zoomPaneSize,
                        height: zoomPaneSize,
                        display: {
                          xs: "none",
                          md:
                            hovering && naturalSize && bgImageCss !== "none"
                              ? "block"
                              : "none",
                        },
                        border: "1px solid #eee",
                        borderRadius: 2,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        backgroundImage: bgImageCss,
                        backgroundRepeat: "no-repeat",
                        backgroundSize: (() => {
                          if (!naturalSize) return "200% 200%";
                          const minZoomByPane = Math.max(
                            zoomPaneSize / naturalSize.w,
                            zoomPaneSize / naturalSize.h
                          );
                          const z = Math.max(zoomFactor, minZoomByPane);
                          return `${naturalSize.w * z}px ${
                            naturalSize.h * z
                          }px`;
                        })(),
                        backgroundPosition: (() => {
                          if (!wrapRef.current || !naturalSize)
                            return `${bgPos.x * 100}% ${bgPos.y * 100}%`;
                          const xRatio = Math.min(Math.max(bgPos.x, 0), 1);
                          const yRatio = Math.min(Math.max(bgPos.y, 0), 1);
                          const minZoomByPane = Math.max(
                            zoomPaneSize / naturalSize.w,
                            zoomPaneSize / naturalSize.h
                          );
                          const z = Math.max(zoomFactor, minZoomByPane);
                          const bgW = naturalSize.w * z;
                          const bgH = naturalSize.h * z;
                          const posX = -(xRatio * (bgW - zoomPaneSize));
                          const posY = -(yRatio * (bgH - zoomPaneSize));
                          return `${posX}px ${posY}px`;
                        })(),
                        zIndex: 20,
                        pointerEvents: "none",
                        backgroundColor: "#fff",
                      }}
                    />
                  </Box>

                  <Grid container spacing={1} justifyContent="center">
                    {images.map((img, i) => (
                      <Grid item key={i}>
                        <Box
                          component="img"
                          src={img}
                          alt={`${data.title} ${i + 1}`}
                          onClick={() => setActiveImage(i)}
                          sx={{
                            width: 84,
                            height: 84,
                            objectFit: "cover",
                            borderRadius: 1,
                            cursor: "pointer",
                            border:
                              i === activeImage
                                ? "2px solid #d32f2f"
                                : "1px solid #eee",
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}

              {mainTab === 1 && has3DModel && data.model3d && (
                <Box
                  sx={{
                    border: "1px solid #eee",
                    borderRadius: 2,
                    overflow: "hidden",
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <ModelViewer
                    src={data.model3d.url}
                    type={data.model3d.type}
                    height={420}
                  />
                </Box>
              )}

              {mainTab === 1 && !has3DModel && (
                <Box
                  sx={{
                    height: 420,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed #ddd",
                    borderRadius: 2,
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Typography color="text.secondary">
                    {t("productModal.no3d")}
                  </Typography>
                </Box>
              )}

              {/* Teknik Özellikler sekmesi kaldırıldı; sağ panel altında gösterilecek */}
            </Grid>
            <Grid
              item
              xs={12}
              md={7}
              sx={{ position: "relative", height: { md: "75vh" } }}
            >
              <Box
                sx={{
                  p: 3,
                  backgroundColor: "white",
                  borderRadius: 2,
                  border: "1px solid #eee",
                  mt: 0,
                  transition: "margin-top 0.2s ease",
                  maxWidth: { md: 640 },
                  ml: { md: 2 },
                  height: { md: "100%" },
                  overflowY: { md: "auto" },
                  pr: { md: 1 },
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <Chip label={data.fireRating} color="primary" />
                  <Typography variant="body2" color="text.secondary">
                    {t("productModal.fireResistance")}
                  </Typography>
                </Box>
                {data.showPrice && data.price && (
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 800, mb: 1, color: "primary.main" }}
                  >
                    {i18n.language.startsWith("tr")
                      ? `₺${data.price.toLocaleString("tr-TR")}`
                      : `${data.price.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}`}
                  </Typography>
                )}
                <Typography
                  color="text.secondary"
                  sx={{ mb: 2, lineHeight: 1.6 }}
                >
                  {data.description || t("products.subtitle")}
                </Typography>

                {data.features && (
                  <>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      {t("productModal.features")}
                    </Typography>
                    <List dense sx={{ mb: 2 }}>
                      {data.features
                        .slice(0, 4)
                        .map((feature: string, i: number) => (
                        <ListItem key={i} sx={{ py: 0.5, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircle
                              color="success"
                              sx={{ fontSize: 18 }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{ variant: "body2" }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Button variant="contained" size="large" fullWidth>
                    {t("productModal.requestQuote")}
                  </Button>
                  <Button variant="outlined" size="large" fullWidth>
                    {t("productModal.contact")}
                  </Button>
                  {has3DModel && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setMainTab(1)}
                      sx={{ alignSelf: "center" }}
                    >
                      {t("productModal.view3dButton")}
                    </Button>
                  )}
                </Box>

                {data.specifications && (
                  <Box sx={{ mt: 3 }}>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                      {t("productModal.specs.title")}
                    </Typography>
                    <TableContainer component={Paper} sx={{ mb: 3 }}>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {t("productModal.specs.dimensions")}
                            </TableCell>
                            <TableCell>
                              {data.specifications.dimensions}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {t("productModal.specs.weight")}
                            </TableCell>
                            <TableCell>{data.specifications.weight}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {t("productModal.specs.material")}
                            </TableCell>
                            <TableCell>
                              {data.specifications.material}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {t("productModal.specs.thickness")}
                            </TableCell>
                            <TableCell>
                              {data.specifications.thickness}
                            </TableCell>
                          </TableRow>
                          {data.specifications.glassPanelSize && (
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>
                                {t("productModal.specs.glassPanelSize")}
                              </TableCell>
                              <TableCell>
                                {data.specifications.glassPanelSize}
                              </TableCell>
                            </TableRow>
                          )}
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {t("productModal.specs.lockType")}
                            </TableCell>
                            <TableCell>
                              {data.specifications.lockType}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {t("productModal.specs.hingeType")}
                            </TableCell>
                            <TableCell>
                              {data.specifications.hingeType}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {t("productModal.specs.fireResistance")}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={data.specifications.fireResistance}
                                color="error"
                                size="small"
                                icon={<Schedule />}
                              />
                            </TableCell>
                          </TableRow>
                          {data.specifications.soundInsulation && (
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>
                                {t("productModal.specs.soundInsulation")}
                              </TableCell>
                              <TableCell>
                                {data.specifications.soundInsulation}
                              </TableCell>
                            </TableRow>
                          )}
                          {data.specifications.thermalInsulation && (
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>
                                {t("productModal.specs.thermalInsulation")}
                              </TableCell>
                              <TableCell>
                                {data.specifications.thermalInsulation}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Palette /> {t("productModal.specs.colorOptions")}
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}
                    >
                      {data.specifications.colorOptions.map(
                        (color: string, i: number) => (
                        <Chip
                          key={i}
                          label={color}
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Box>

                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Security /> {t("productModal.specs.certificates")}
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}
                    >
                      {data.specifications.certificates.map(
                        (cert: string, i: number) => (
                        <Chip
                          key={i}
                          label={cert}
                          color="success"
                          size="small"
                        />
                      ))}
                    </Box>

                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Build /> {t("productModal.specs.testStandards")}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {data.specifications.testStandards.map(
                        (standard: string, i: number) => (
                        <Chip
                          key={i}
                          label={standard}
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ProductModal;
